import NoResults from '@/components/Elements/NoResults/NoResults';
import { Event } from '@/types';
import { dateFromApiDateTime } from '@/utils/format';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { ShiftEventCard } from './ShiftEventCard';

interface ShiftEventSectionProps {
  events: Event[];
  depotAddress: string | null;
}

function formatEventAddress(event: Event): string {
  return [event.address, event.city, event.state, event.zipcode].filter(Boolean).join(', ');
}

/**
 * Finds the index of the "next" event — the first event that hasn't started yet.
 * Returns -1 if no upcoming event exists (all events are happening or over).
 */
function findNextEventIndex(events: Event[]): number {
  const now = new Date();

  // If any event is currently happening, there's no "next" — the current one shows "Now"
  const hasHappeningEvent = events.some(
    (event) =>
      dateFromApiDateTime(event.start_at) <= now && dateFromApiDateTime(event.end_at) > now,
  );

  if (hasHappeningEvent) return -1;

  // Otherwise, find the first event that hasn't ended yet (i.e., upcoming)
  return events.findIndex((event) => dateFromApiDateTime(event.end_at) > now);
}

export const ShiftEventSection = ({ events, depotAddress }: ShiftEventSectionProps) => {
  const nextEventIndex = useMemo(() => findNextEventIndex(events), [events]);

  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <CalendarDaysIcon className="w-5 h-5 text-foreground/60" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground/60">
          Your Events Today ({events.length})
        </h3>
      </div>

      {events.length === 0 ? (
        <NoResults label="events for today" />
      ) : (
        <div className="space-y-2.5">
          {events.map((event, index) => {
            // First event: navigate from the depot. Subsequent: from previous event.
            const originAddress =
              index === 0 ? depotAddress : formatEventAddress(events[index - 1]);

            return (
              <ShiftEventCard
                key={event.id}
                event={event}
                isNext={index === nextEventIndex}
                originAddress={originAddress}
              />
            );
          })}
        </div>
      )}
    </section>
  );
};
